// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkspacePage from "@/pages/WorkspacePage";
import CoursePage from "@/pages/CoursePage";
import CourseEditorPage from "@/pages/CourseEditorPage";
import SectionViewPage from "@/pages/SectionViewPage";
import TPPage from "@/pages/TPPage";
import TPEditorPage from "@/pages/TPEditorPage";
import TPContentPage from "@/pages/TPContentPage";
import QuizPage from "@/pages/QuizPage";
import QuizEditorPage from "@/pages/QuizEditorPage";
import QuizTakePage from "@/pages/QuizTakePage";
import AssignmentPage from "@/pages/AssignmentPage";
import UrlResourcesPage from "@/pages/UrlResourcesPage";
import AssignmentEditorPage from "@/pages/AssignmentEditorPage";
import AssignmentDetailPage from "@/pages/AssignmentDetailPage";
import MeetingsPage from "@/pages/MeetingsPage";
import MeetingRoomPage from "@/pages/MeetingRoomPage";
import ScreenMonitorPage from "@/pages/ScreenMonitorPage";
import ChatPage from "@/pages/ChatPage";
import FilesPage from "@/pages/FilesPage";
import ParticipantsPage from "@/pages/ParticipantsPage";
import ProgressPage from "@/pages/ProgressPage";
import AdminDashboard from "@/pages/AdminDashboard";
import StaffChatPage from "@/pages/StaffChatPage";
import ShareViewPage from "@/pages/ShareViewPage";
import ProfilePage from "@/pages/ProfilePage";
import DrawPage from "@/pages/draw/DrawPage";
import DiagramListPage from "@/pages/draw/DiagramListPage";
import StaticSiteDeployerPage from "@/pages/StaticSiteDeployerPage";
import CodeSharePage from "@/pages/CodeSharePage";
import ContactPage from "@/pages/ContactPage";
import MonDossierPage from "@/pages/MonDossierPage";
import LiveCodeSharePage from "@/pages/LiveCodeSharePage";
import VideoCapturePage from "@/pages/VideoCapturePage";
import PlanningPage from "@/pages/PlanningPage";
import AttendancePage from "@/pages/AttendancePage";
import AbsenceRequestPage from "@/pages/AbsenceRequestPage";
import LabCatalogPage from "@/pages/LabCatalogPage";
import LabWorkspacePage from "@/pages/LabWorkspacePage";
import EvaluationsPage from "@/pages/EvaluationsPage";
import ReportsPage from "@/pages/ReportsPage";
import VMsPage from "@/pages/VMsPage";
import DocumentationPage from "@/pages/DocumentationPage";

// Notes Module
import NotesPage from "@/pages/notes/NotesPage";

// Training Sessions Pages
import TrainingSessionsPage from "@/pages/training/TrainingSessionsPage";
import TrainingSessionEditorPage from "@/pages/training/TrainingSessionEditorPage";
import SessionRegistrationPage from "@/pages/training/SessionRegistrationPage";
import LLMConfigPage from "@/pages/training/LLMConfigPage";

// Exams & Certifications Pages
import ExamListPage from "@/pages/exams/ExamListPage";
import ExamEditorPage from "@/pages/exams/ExamEditorPage";
import QuestionBankPage from "@/pages/exams/QuestionBankPage";
import QuestionEditorPage from "@/pages/exams/QuestionEditorPage";
import ExamTakePage from "@/pages/exams/ExamTakePage";
import ExamResultsPage from "@/pages/exams/ExamResultsPage";
import ExamDetailPage from "@/pages/exams/ExamDetailPage";
import PublicExamRegistrationPage from "@/pages/exams/PublicExamRegistrationPage";
import PublicExamTakePage from "@/pages/exams/PublicExamTakePage";
import PublicExamResultsPage from "@/pages/exams/PublicExamResultsPage";
import ExamStatsPage from "@/pages/exams/ExamStatsPage";
import ExamAttemptsPage from "@/pages/exams/ExamAttemptsPage";

// Public Pages
import PublicCodeSharePage from "@/pages/public/PublicCodeSharePage";

// Interview Module Pages
import InterviewsPage from "@/pages/interviews/InterviewsPage";
import InterviewQuestionsPage from "@/pages/interviews/InterviewQuestionsPage";
import InterviewTemplatesPage from "@/pages/interviews/InterviewTemplatesPage";
import InterviewTemplatePage from "@/pages/interviews/InterviewTemplatePage";
import InterviewCandidatesPage from "@/pages/interviews/InterviewCandidatesPage";
import InterviewCandidatePage from "@/pages/interviews/InterviewCandidatePage";
import InterviewLivePage from "@/pages/interviews/InterviewLivePage";
import InterviewSummaryPage from "@/pages/interviews/InterviewSummaryPage";
import InterviewImportPage from "@/pages/interviews/InterviewImportPage";

// Webinar / Live Classes Module
import WebinarListPage from "@/pages/webinars/WebinarListPage";
import WebinarEditorPage from "@/pages/webinars/WebinarEditorPage";
import WebinarLivePage from "@/pages/webinars/WebinarLivePage";
import WebinarAnalyticsPage from "@/pages/webinars/WebinarAnalyticsPage";

// Webinar Sessions (Public Registration)
import WebinarSessionListPage from "@/pages/webinars/WebinarSessionListPage";
import WebinarSessionEditorPage from "@/pages/webinars/WebinarSessionEditorPage";
import WebinarSessionRegistrationsPage from "@/pages/webinars/WebinarSessionRegistrationsPage";
import PublicWebinarSessionPage from "@/pages/webinars/PublicWebinarSessionPage";
import PublicWebinarLivePage from "@/pages/webinars/PublicWebinarLivePage";

// Temporary Public Sessions Pages
import TempSessionEditorPage from "@/pages/temp_sessions/TempSessionEditorPage";
import TempSessionAttendancePage from "@/pages/temp_sessions/TempSessionAttendancePage";
import TempSessionLogsPage from "@/pages/temp_sessions/TempSessionLogsPage";
import PublicTempSessionPage from "@/pages/temp_sessions/PublicTempSessionPage";
import SessionDomainErrorPage from "@/pages/temp_sessions/SessionDomainErrorPage";

// AI Recording Studio Pro Module
import RecordingStudioDashboard from "@/pages/recording-studio/RecordingStudioDashboard";
import RecordingStudioPage from "@/pages/recording-studio/RecordingStudioPage";
import RecordingProjectsPage from "@/pages/recording-studio/RecordingProjectsPage";
import RecordingSSHManager from "@/pages/recording-studio/RecordingSSHManager";
import RecordingAIAssistant from "@/pages/recording-studio/RecordingAIAssistant";
import RecordingVoiceAI from "@/pages/recording-studio/RecordingVoiceAI";
import RecordingVideoEditor from "@/pages/recording-studio/RecordingVideoEditor";
import RecordingExport from "@/pages/recording-studio/RecordingExport";
import RecordingSettings from "@/pages/recording-studio/RecordingSettings";

// Showcase / Vitrine
import ShowcaseAdminPage from "@/pages/showcase/ShowcaseAdminPage";
import VitrinePage from "@/pages/public/VitrinePage";

// Tutorials Module (copié depuis EFIlearn)
import TutorialEditorPage from "@/pages/tutorials/TutorialEditorPage";
import TutorialPublicPage from "@/pages/tutorials/TutorialPublicPage";

import "@/App.css";

// ========================== SESSION DOMAIN DETECTION ==========================
// Check if the current hostname matches a known session domain.
// The share_domain is configured per-tenant in Admin > Configuration du Partage.
// We also cache it in sessionStorage to avoid re-fetching on every page load.
const SESSION_DOMAINS_KEY = '__efi_session_domains';

function getIsSessionDomain() {
  const hostname = window.location.hostname;
  // Quick check against cached known session domains
  try {
    const cached = JSON.parse(sessionStorage.getItem(SESSION_DOMAINS_KEY) || '[]');
    if (cached.includes(hostname)) return true;
  } catch { /* ignore */ }
  return false;
}

// Fetch the share_domain from the backend and cache it (runs once at startup)
(async function detectSessionDomain() {
  try {
    const hostname = window.location.hostname;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    if (!API_URL) return;
    // If already detected, skip the fetch
    if (getIsSessionDomain()) return;
    const res = await fetch(`${API_URL}/api/share/public-domains`);
    if (!res.ok) return;
    const data = await res.json();
    const domains = (data.session_domains || []).map(d => d.toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, ''));
    if (domains.length > 0) {
      sessionStorage.setItem(SESSION_DOMAINS_KEY, JSON.stringify(domains));
      // If we just discovered we're on a session domain, reload to apply session-only mode
      if (domains.includes(hostname)) {
        window.location.reload();
      }
    }
  } catch { /* fail silently */ }
})();

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, allowedRoles = null }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// ========================== SESSION DOMAIN ROUTES ==========================
// When accessed from the session domain, only /session/* routes are available.
function SessionDomainRoutes() {
  return (
    <Routes>
      <Route path="/session/*" element={<PublicTempSessionPage />} />
      <Route path="/s/:token" element={<PublicTempSessionPage />} />
      {/* Public Exam routes also allowed on session domain */}
      <Route path="/exam/:slug/register" element={<PublicExamRegistrationPage />} />
      <Route path="/exam/:slug/take/:attemptId" element={<PublicExamTakePage />} />
      <Route path="/exam/:slug/results/:attemptId" element={<PublicExamResultsPage />} />
      <Route path="*" element={<SessionDomainErrorPage />} />
    </Routes>
  );
}

function AppRoutes() {
  const { user } = useAuth();

  // Block DevTools for non-admin users
  useEffect(() => {
    const isAdmin = user?.role === 'admin';
    if (isAdmin) return; // Admins keep full access

    const blockKeys = (e) => {
      // F12
      if (e.key === 'F12') { e.preventDefault(); return; }
      // Ctrl+Shift+I / Ctrl+Shift+J / Ctrl+Shift+C
      if (e.ctrlKey && e.shiftKey && ['I','J','C'].includes(e.key.toUpperCase())) { e.preventDefault(); return; }
      // Ctrl+U (view source)
      if (e.ctrlKey && e.key.toUpperCase() === 'U') { e.preventDefault(); return; }
    };

    const blockContextMenu = (e) => e.preventDefault();

    document.addEventListener('keydown', blockKeys, true);
    document.addEventListener('contextmenu', blockContextMenu, true);

    return () => {
      document.removeEventListener('keydown', blockKeys, true);
      document.removeEventListener('contextmenu', blockContextMenu, true);
    };
  }, [user]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Public CodeShare */}
      <Route path="/share/code/:snippetId" element={<PublicCodeSharePage />} />
      
      {/* Live CodeShare (real-time collaboration) */}
      <Route path="/live/:sessionId" element={<LiveCodeSharePage />} />

      {/* Public Share Routes (no auth required) */}
      <Route path="/share/:token" element={<ShareViewPage />} />
      <Route path="/view/:token" element={<ShareViewPage />} />
      
      {/* Public Vitrine (boutique + portfolio) */}
      <Route path="/vitrine" element={<VitrinePage />} />

      {/* Public Tutorial View */}
      <Route path="/t/:pageId" element={<TutorialPublicPage />} />

      {/* Public Training Session Registration */}
      <Route path="/sessions/:slug/inscription" element={<SessionRegistrationPage />} />

      {/* Public Exam Registration */}
      <Route path="/exam/:slug/register" element={<PublicExamRegistrationPage />} />
      <Route path="/exam/:slug/take/:attemptId" element={<PublicExamTakePage />} />
      <Route path="/exam/:slug/results/:attemptId" element={<PublicExamResultsPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      
      {/* Workspace Routes */}
      <Route path="/workspace/:workspaceId" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/courses" element={<ProtectedRoute><CoursePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/courses/new" element={<ProtectedRoute><CourseEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/courses/:courseId" element={<ProtectedRoute><CourseEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/courses/:courseId/view" element={<ProtectedRoute><CourseEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/courses/:courseId/sections/:sectionId" element={<ProtectedRoute><SectionViewPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/tp" element={<ProtectedRoute><TPPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/tp/new" element={<ProtectedRoute><TPEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/tp/:tpId" element={<ProtectedRoute><TPEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/tp/:tpId/view" element={<ProtectedRoute><TPContentPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/quiz" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/quiz/new" element={<ProtectedRoute><QuizEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/quiz/:quizId/edit" element={<ProtectedRoute><QuizEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/quiz/:quizId/take" element={<ProtectedRoute><QuizTakePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/assignments" element={<ProtectedRoute><AssignmentPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/assignments/new" element={<ProtectedRoute><AssignmentEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/assignments/:assignmentId/edit" element={<ProtectedRoute><AssignmentEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/assignments/:assignmentId" element={<ProtectedRoute><AssignmentDetailPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/url-resources" element={<ProtectedRoute><UrlResourcesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/meetings" element={<ProtectedRoute><MeetingsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/meetings/:meetingId" element={<ProtectedRoute><MeetingRoomPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/meetings/:meetingId/monitor" element={<ProtectedRoute allowedRoles={['admin', 'trainer', 'manager']}><ScreenMonitorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/files" element={<ProtectedRoute><FilesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/participants" element={<ProtectedRoute><ParticipantsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/progress" element={<ProtectedRoute><ProgressPage /></ProtectedRoute>} />
      
      {/* DevOpsLearn Draw */}
      <Route path="/workspace/:workspaceId/draw" element={<ProtectedRoute><DiagramListPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/draw/new" element={<ProtectedRoute><DrawPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/draw/:diagramId" element={<ProtectedRoute><DrawPage /></ProtectedRoute>} />

      {/* Static Site Deployer */}
      <Route path="/workspace/:workspaceId/sites" element={<ProtectedRoute><StaticSiteDeployerPage /></ProtectedRoute>} />

      {/* CodeShare */}
      <Route path="/workspace/:workspaceId/codeshare" element={<ProtectedRoute><CodeSharePage /></ProtectedRoute>} />

      {/* Video Capture */}
      <Route path="/workspace/:workspaceId/videos" element={<ProtectedRoute><VideoCapturePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/planning" element={<ProtectedRoute><PlanningPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/attendance" element={<ProtectedRoute><AttendancePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/absence-requests" element={<ProtectedRoute><AbsenceRequestPage /></ProtectedRoute>} />

      {/* Interactive Labs */}
      <Route path="/workspace/:workspaceId/labs" element={<ProtectedRoute><LabCatalogPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/labs/:labId" element={<ProtectedRoute><LabWorkspacePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/vms" element={<ProtectedRoute><VMsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/evaluations" element={<ProtectedRoute><EvaluationsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />

      {/* Notes Module */}
      <Route path="/workspace/:workspaceId/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />

      {/* Webinar / Live Classes Module (Admin Global) */}
      <Route path="/admin/webinars" element={<ProtectedRoute><WebinarListPage /></ProtectedRoute>} />
      <Route path="/admin/webinars/new" element={<ProtectedRoute><WebinarEditorPage /></ProtectedRoute>} />
      <Route path="/admin/webinars/:webinarId/edit" element={<ProtectedRoute><WebinarEditorPage /></ProtectedRoute>} />
      <Route path="/admin/webinars/:webinarId/live" element={<ProtectedRoute><WebinarLivePage /></ProtectedRoute>} />
      <Route path="/admin/webinars/:webinarId/analytics" element={<ProtectedRoute><WebinarAnalyticsPage /></ProtectedRoute>} />

      {/* Webinar Sessions (Admin) */}
      <Route path="/admin/webinar-sessions" element={<ProtectedRoute><WebinarSessionListPage /></ProtectedRoute>} />
      <Route path="/admin/webinar-sessions/new" element={<ProtectedRoute><WebinarSessionEditorPage /></ProtectedRoute>} />
      <Route path="/admin/webinar-sessions/:sessionId/edit" element={<ProtectedRoute><WebinarSessionEditorPage /></ProtectedRoute>} />
      <Route path="/admin/webinar-sessions/:sessionId/registrations" element={<ProtectedRoute><WebinarSessionRegistrationsPage /></ProtectedRoute>} />

      {/* Webinar Sessions (Public — no auth) */}
      <Route path="/webinar-session/:slug" element={<PublicWebinarSessionPage />} />
      <Route path="/webinar-live/:token" element={<PublicWebinarLivePage />} />

      {/* Interview Module */}
      <Route path="/workspace/:workspaceId/interviews" element={<ProtectedRoute><InterviewsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/questions" element={<ProtectedRoute><InterviewQuestionsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/import" element={<ProtectedRoute><InterviewImportPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/templates" element={<ProtectedRoute><InterviewTemplatesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/templates/:templateId" element={<ProtectedRoute><InterviewTemplatePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/candidates" element={<ProtectedRoute><InterviewCandidatesPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/candidates/:candidateId" element={<ProtectedRoute><InterviewCandidatePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/live/:interviewId" element={<ProtectedRoute><InterviewLivePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/interviews/summary/:interviewId" element={<ProtectedRoute><InterviewSummaryPage /></ProtectedRoute>} />

      {/* Workspace Exams */}
      <Route path="/workspace/:workspaceId/exams" element={<ProtectedRoute><ExamListPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/new" element={<ProtectedRoute><ExamEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/questions" element={<ProtectedRoute><QuestionBankPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/questions/new" element={<ProtectedRoute><QuestionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/questions/:questionId" element={<ProtectedRoute><QuestionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/:examId" element={<ProtectedRoute><ExamDetailPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/:examId/edit" element={<ProtectedRoute><ExamEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/:examId/stats" element={<ProtectedRoute><ExamStatsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/:examId/attempts" element={<ProtectedRoute><ExamAttemptsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/exams/:examId/take" element={<ProtectedRoute><ExamTakePage /></ProtectedRoute>} />

      {/* Training Sessions Module */}
      <Route path="/workspace/:workspaceId/training" element={<ProtectedRoute><TrainingSessionsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/training/new" element={<ProtectedRoute><TrainingSessionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/training/:sessionId" element={<ProtectedRoute><TrainingSessionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/training/:sessionId/registrations" element={<ProtectedRoute><TrainingSessionsPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/llm-config" element={<ProtectedRoute><LLMConfigPage /></ProtectedRoute>} />

      {/* Global Training Sessions Routes (Admin Dashboard level) */}
      <Route path="/training" element={<ProtectedRoute adminOnly><TrainingSessionsPage /></ProtectedRoute>} />
      <Route path="/training/new" element={<ProtectedRoute adminOnly><TrainingSessionEditorPage /></ProtectedRoute>} />
      <Route path="/training/:sessionId" element={<ProtectedRoute adminOnly><TrainingSessionEditorPage /></ProtectedRoute>} />
      <Route path="/admin/vitrine" element={<ProtectedRoute adminOnly><ShowcaseAdminPage /></ProtectedRoute>} />
      <Route path="/llm-config" element={<ProtectedRoute adminOnly><LLMConfigPage /></ProtectedRoute>} />

      {/* Tutorials Module (Admin editor) */}
      <Route path="/tutorials/:pageId" element={<ProtectedRoute adminOnly><TutorialEditorPage /></ProtectedRoute>} />

      {/* Exam Results (workspace scoped) */}
      <Route path="/workspace/:workspaceId/exams/results/:attemptId" element={<ProtectedRoute><ExamResultsPage /></ProtectedRoute>} />

      {/* Contact Route */}
      <Route path="/contact" element={<ProtectedRoute><ContactPage /></ProtectedRoute>} />

      {/* Mon Dossier - Candidate onboarding */}
      <Route path="/mon-dossier" element={<ProtectedRoute><MonDossierPage /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'trainer']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/documentation" element={<ProtectedRoute><DocumentationPage /></ProtectedRoute>} />
      <Route path="/staff-chat" element={<ProtectedRoute allowedRoles={['admin', 'manager', 'trainer']}><StaffChatPage /></ProtectedRoute>} />

      {/* AI Recording Studio Pro */}
      <Route path="/studio" element={<ProtectedRoute allowedRoles={['admin']}><RecordingStudioDashboard /></ProtectedRoute>} />
      <Route path="/studio/record" element={<ProtectedRoute allowedRoles={['admin']}><RecordingStudioPage /></ProtectedRoute>} />
      <Route path="/studio/projects" element={<ProtectedRoute allowedRoles={['admin']}><RecordingProjectsPage /></ProtectedRoute>} />
      <Route path="/studio/ssh" element={<ProtectedRoute allowedRoles={['admin']}><RecordingSSHManager /></ProtectedRoute>} />
      <Route path="/studio/ai" element={<ProtectedRoute allowedRoles={['admin']}><RecordingAIAssistant /></ProtectedRoute>} />
      <Route path="/studio/voice" element={<ProtectedRoute allowedRoles={['admin']}><RecordingVoiceAI /></ProtectedRoute>} />
      <Route path="/studio/editor" element={<ProtectedRoute allowedRoles={['admin']}><RecordingVideoEditor /></ProtectedRoute>} />
      <Route path="/studio/export" element={<ProtectedRoute allowedRoles={['admin']}><RecordingExport /></ProtectedRoute>} />
      <Route path="/studio/settings" element={<ProtectedRoute allowedRoles={['admin']}><RecordingSettings /></ProtectedRoute>} />

      {/* Temporary Public Sessions Module */}
      <Route path="/workspace/:workspaceId/temp-sessions/new" element={<ProtectedRoute><TempSessionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/temp-sessions/:sessionId" element={<ProtectedRoute><TempSessionEditorPage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/temp-sessions/:sessionId/attendance" element={<ProtectedRoute><TempSessionAttendancePage /></ProtectedRoute>} />
      <Route path="/workspace/:workspaceId/temp-sessions/:sessionId/logs" element={<ProtectedRoute><TempSessionLogsPage /></ProtectedRoute>} />

      {/* Public Temporary Session Access */}
      <Route path="/s/:token" element={<PublicTempSessionPage />} />
      <Route path="/session/*" element={<PublicTempSessionPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  const isSessionMode = getIsSessionDomain();

  return (
    <AuthProvider>
      <BrowserRouter>
        {isSessionMode ? <SessionDomainRoutes /> : <AppRoutes />}
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
